'use client';

import { useState } from 'react';

export default function CalculatorDate() {
  const today = new Date()?.toISOString()?.split('T')?.[0];
  const thirtyDaysLater = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
  
  const INITIAL_STATE = {
    startDate: today,
    endDate: today,
  };

  const PRESETS = {
    conservative: {
      startDate: today,
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
    },
    moderate: {
      startDate: today,
      endDate: thirtyDaysLater,
    },
    aggressive: {
      startDate: today,
      endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
    },
  };

  const [startDate, setStartDate] = useState(INITIAL_STATE.startDate);
  const [endDate, setEndDate] = useState(INITIAL_STATE.endDate);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleChange = () => {
    setIsCalculating(true);
    setTimeout(() => setIsCalculating(false), 800);
  };

  const calculateDifference = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end?.getTime() - start?.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30.44);
    const diffYears = Math.floor(diffDays / 365.25);
    
    return { days: diffDays, weeks: diffWeeks, months: diffMonths, years: diffYears };
  };

  const diff = calculateDifference();

  const handleReset = () => {
    setStartDate(INITIAL_STATE.startDate);
    setEndDate(INITIAL_STATE.endDate);
  };

  const handlePreset = (preset: keyof typeof PRESETS) => {
    const values = PRESETS[preset];
    setStartDate(values.startDate);
    setEndDate(values.endDate);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
        {isCalculating && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-emerald-700 font-semibold">Calculating...</span>
            </div>
          </div>
        )}

        {/* Preset and Reset Buttons */}
        <div className="mb-6 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handlePreset('conservative')}
              className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
            >
              Conservative
            </button>
            <button
              onClick={() => handlePreset('moderate')}
              className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
            >
              Moderate
            </button>
            <button
              onClick={() => handlePreset('aggressive')}
              className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
            >
              Aggressive
            </button>
          </div>
          <button
            onClick={handleReset}
            className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
          >
            Reset to Default
          </button>
        </div>

        <div className="space-y-6 mb-8">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e?.target?.value); handleChange(); }}
              className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e?.target?.value); handleChange(); }}
              className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-gradient-to-br from-lime-500 via-green-500 to-emerald-600 rounded-xl p-6 text-white text-center">
            <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">Days Between</div>
            <div className="text-5xl font-bold">{diff?.days}</div>
            <div className="text-sm opacity-90 mt-2">days</div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-xs text-slate-600 mb-1">Weeks</div>
              <div className="text-xl font-bold text-slate-900">{diff?.weeks}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-xs text-slate-600 mb-1">Months</div>
              <div className="text-xl font-bold text-slate-900">{diff?.months}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-xs text-slate-600 mb-1">Years</div>
              <div className="text-xl font-bold text-slate-900">{diff?.years}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}